import React, { useState, useEffect } from 'react';
import { useTree } from '../contexts/TreeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { formatBytes } from './AdminDashboard';
import { Icons } from './Icons';
import useMobile from '../utils/useMobile';
import { track } from '../utils/analytics';

// Floats above the canvas rather than a full-width top bar, matching the
// existing fixed-position FAB/adminBtn pattern — avoids reworking the app
// shell's 100vh layout just to make room for a persistent header.
export default function QuotaNudgeBanner({ onUpgradeClick }) {
  const { peopleCount, storageBytes } = useTree();
  const { accessState, tier, maxPeople, maxStorageBytes } = useSubscription();
  const isMobile = useMobile();
  const [dismissed, setDismissed] = useState(false);

  const peoplePct  = maxPeople ? peopleCount / maxPeople : 0;
  const storagePct = maxStorageBytes ? storageBytes / maxStorageBytes : 0;
  const pct = Math.max(peoplePct, storagePct);
  const atLimit = pct >= 1;

  // AccessWall owns messaging once access isn't full; paid accounts have
  // much higher headroom so a nudge here would rarely fire and isn't the point.
  // The hard-limit message can't be dismissed away.
  const applies = accessState === 'full' && tier !== 'paid' && pct >= 0.8;
  const visible = applies && (atLimit || !dismissed);

  // The conditions above used to be early returns, which is the natural shape
  // for a component that renders nothing most of the time — but a hook cannot
  // live below one, and this event has to be logged where the banner is
  // actually shown rather than where something decides to render it.
  //
  // Deliberately not keyed on peopleCount: the interesting fact is that the
  // nudge appeared, and re-firing on every person added would report one
  // encounter as dozens. Crossing from approaching to reached is a second
  // appearance, which is why atLimit is a dependency.
  useEffect(() => {
    if (!visible) return;
    track('quota_nudge_shown', {
      people_count: peopleCount,
      limit: maxPeople,
      at_limit: atLimit ? 'yes' : 'no',
    });
  }, [visible, atLimit]); // eslint-disable-line

  if (!visible) return null;

  const reason = peoplePct >= storagePct
    ? `${peopleCount} of ${maxPeople} people`
    : `${formatBytes(storageBytes)} of ${formatBytes(maxStorageBytes)} storage`;

  return (
    <div style={{
      position: 'fixed',
      bottom: isMobile ? 84 : 20,
      left: isMobile ? 12 : 296,
      right: isMobile ? 12 : 'auto',
      maxWidth: isMobile ? 'none' : 420,
      zIndex: 120,
      background: atLimit ? '#C0392B' : '#2C2418',
      color: '#F5EDE3',
      borderRadius: 12,
      padding: '12px 14px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <span style={{ color: atLimit ? '#fff' : '#C4975A', flexShrink: 0, display: 'flex' }}>
        <Icons.AlertTriangle size={18} />
      </span>
      <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.4 }}>
        {atLimit
          ? `You've reached your free plan limit (${reason}). Upgrade to keep adding.`
          : `You're approaching your free plan limit (${reason}).`}
      </span>
      <button
        onClick={onUpgradeClick}
        style={{
          flexShrink: 0, background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
          border: 'none', borderRadius: 7, color: '#2C2418', fontSize: 12, fontWeight: 700,
          padding: '7px 12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
        }}
      >
        Upgrade
      </button>
      {!atLimit && (
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          style={{
            flexShrink: 0, background: 'none', border: 'none', color: 'rgba(245,237,227,0.6)',
            cursor: 'pointer', padding: 2, display: 'flex',
          }}
        >
          <Icons.Close size={14} />
        </button>
      )}
    </div>
  );
}
